import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Heart, MessageCircle, Share2, Volume2, VolumeX, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function Shorts() {
  const [videos, setVideos] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [params] = useSearchParams();
  const containerRef = useRef(null);
  const startId = params.get("v");

  useEffect(() => {
    api.get("/videos", { params: { format: "short", limit: 50 } }).then(({data}) => {
      let list = data;
      if (startId) {
        const idx = list.findIndex(v => v.id === startId);
        if (idx > 0) list = [list[idx], ...list.slice(0,idx), ...list.slice(idx+1)];
      }
      setVideos(list);
    });
  }, [startId]);

  // Warmup: preload the next 2 videos while current is playing so swipes are instant.
  const warmup = [videos[activeIndex + 1], videos[activeIndex + 2]].filter(w => w && w.video_url);

  return (
    <div className="fixed inset-0 bg-black overflow-hidden">
      <Link to="/" className="absolute top-4 left-4 z-30 p-2 rounded-full bg-black/50 backdrop-blur-md" data-testid="shorts-back-btn">
        <ArrowLeft size={20}/>
      </Link>
      <div className="absolute top-4 right-4 z-30 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-md text-xs font-extrabold text-[#FF3EA5] tracking-wide">LOOP</div>
      <div ref={containerRef} className="h-[100dvh] overflow-y-scroll snap-y-feed jx-scroll-x" data-testid="shorts-feed">
        {videos.map((v, i) => (
          <ShortItem key={v.id} v={v} index={i} containerRef={containerRef} onActive={setActiveIndex}/>
        ))}
      </div>
      {/* Hidden warmup video elements — kernel loads first frames + a few seconds of MP4
          so the next swipe starts instantly. */}
      <div aria-hidden="true" style={{position:"absolute", width:0, height:0, overflow:"hidden", opacity:0, pointerEvents:"none"}} data-testid="loop-warmup">
        {warmup.map(w => (
          <video key={`warm-${w.id}`} src={w.video_url} preload="auto" muted playsInline/>
        ))}
      </div>
    </div>
  );
}

function ShortItem({ v, index, containerRef, onActive }) {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const { user } = useAuth();
  const nav = useNavigate();
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(v.likes);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        const vid = videoRef.current;
        if (!vid) return;
        if (e.isIntersecting && e.intersectionRatio > 0.6) {
          vid.play().catch(()=>{});
          onActive?.(index);
          if (index === 0) api.post(`/videos/${v.id}/progress`, { position: 0 }).catch(()=>{});
        } else { vid.pause(); }
      });
    }, { threshold: [0, 0.6, 1] });
    obs.observe(el);
    return () => obs.disconnect();
  }, [v.id, index, onActive]);

  useEffect(() => {
    if (!user) return;
    api.get(`/videos/${v.id}/liked`).then(r => setLiked(r.data.liked)).catch(()=>{});
  }, [v.id, user]);

  const onLike = async () => {
    if (!user) return nav("/login");
    try { const {data} = await api.post(`/videos/${v.id}/like`); setLiked(data.liked); setLikes(l=>l+(data.liked?1:-1)); }
    catch { toast.error("Failed to like"); }
  };
  const onShare = async () => {
    const url = window.location.origin + `/watch/${v.id}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Copy failed"); }
  };

  return (
    <div ref={ref} className="relative h-[100dvh] w-full flex items-center justify-center bg-black snap-start">
      <video ref={videoRef} src={v.video_url || undefined} poster={v.thumbnail_url || undefined} muted={muted} loop playsInline
             className="w-full h-full short-video" data-testid={`short-video-${v.id}`}
             onClick={() => { const vid = videoRef.current; if (vid.paused) vid.play(); else vid.pause(); }}/>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-black/40"/>
      {/* Watermark */}
      <div className="pointer-events-none absolute top-16 right-3 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-md">
        <span className="w-4 h-4 rounded-md bg-[#08D9D6] flex items-center justify-center">
          <svg width="8" height="8" viewBox="0 0 10 10" fill="none"><path d="M2 1L2 9L8 5Z" fill="#000"/></svg>
        </span>
        <span className="text-[10px] font-bold tracking-wide text-white/90">JIXXEO VIDEOS</span>
      </div>
      {/* Right rail actions */}
      <div className="absolute right-3 bottom-28 flex flex-col items-center gap-5 z-10">
        <button onClick={onLike} data-testid={`short-like-${v.id}`} className="flex flex-col items-center gap-1 jx-btn">
          <span className={`w-12 h-12 rounded-full flex items-center justify-center backdrop-blur ${liked ? 'bg-[#FF2E63]' : 'bg-white/10'}`}>
            <Heart size={22} className={liked ? 'fill-white' : ''}/>
          </span>
          <span className="text-xs font-semibold">{likes}</span>
        </button>
        <Link to={`/watch/${v.id}`} className="flex flex-col items-center gap-1 jx-btn" data-testid={`short-comment-${v.id}`}>
          <span className="w-12 h-12 rounded-full flex items-center justify-center bg-white/10 backdrop-blur"><MessageCircle size={20}/></span>
          <span className="text-xs font-semibold">{v.comments_count}</span>
        </Link>
        <button onClick={onShare} className="flex flex-col items-center gap-1 jx-btn" data-testid={`short-share-${v.id}`}>
          <span className="w-12 h-12 rounded-full flex items-center justify-center bg-white/10 backdrop-blur"><Share2 size={20}/></span>
          <span className="text-xs font-semibold">Share</span>
        </button>
        <button onClick={()=>setMuted(m=>!m)} className="w-12 h-12 rounded-full flex items-center justify-center bg-white/10 backdrop-blur jx-btn" data-testid={`short-mute-${v.id}`}>
          {muted ? <VolumeX size={20}/> : <Volume2 size={20}/>}
        </button>
      </div>
      {/* Info */}
      <div className="absolute left-4 right-20 bottom-24 z-10">
        <Link to={`/u/${v.creator?.id}`} className="flex items-center gap-2 mb-2">
          <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#08D9D6] to-[#FF2E63] flex items-center justify-center text-black font-bold">
            {(v.creator?.username || '?')[0].toUpperCase()}
          </span>
          <div>
            <div className="font-bold text-sm">@{v.creator?.username}</div>
            <div className="text-[11px] text-white/70">{v.category}</div>
          </div>
        </Link>
        <h3 className="text-sm font-semibold leading-snug line-clamp-2">{v.title}</h3>
      </div>
    </div>
  );
}
